import React from "react";
import {
  CATEGORY_FILTER_OPTIONS,
  CategoryFilterOption,
  CategoryFilters,
  LOCKABLE_FACETS,
} from "../../model/Category";

interface CategoryFiltersEditorProps {
  value: CategoryFilters | null;
  onChange: (next: CategoryFilters | null) => void;
}

interface Row {
  id: string;
  key: string;
  raw: string;
  min: string;
  max: string;
}

const kindOf = (key: string): CategoryFilterOption | undefined =>
  CATEGORY_FILTER_OPTIONS.find((o) => o.key === key);

function toRows(filters: CategoryFilters | null): Row[] {
  if (!filters || typeof filters !== "object") return [];
  const search =
    filters.search &&
    typeof filters.search === "object" &&
    !Array.isArray(filters.search)
      ? filters.search
      : {};

  return Object.entries(search).map(([key, value], i) => {
    if (
      kindOf(key)?.kind === "range" &&
      Array.isArray(value) &&
      value.length > 0 &&
      value[0] &&
      typeof value[0] === "object" &&
      !Array.isArray(value[0])
    ) {
      const r = value[0] as { min_price?: number; max_price?: number };
      return {
        id: `${key}-${i}`,
        key,
        raw: "",
        min: r.min_price != null ? String(r.min_price) : "",
        max: r.max_price != null ? String(r.max_price) : "",
      };
    }
    const isPrimitive = (candidate: unknown): boolean =>
      typeof candidate === "string" || typeof candidate === "number";

    return {
      id: `${key}-${i}`,
      key,
      raw: Array.isArray(value)
        ? value.filter(isPrimitive).join(", ")
        : isPrimitive(value)
          ? String(value)
          : "",
      min: "",
      max: "",
    };
  });
}

function toFilters(
  rows: Row[],
  lockedFacets: string[],
): CategoryFilters | null {
  const search: CategoryFilters["search"] = {};

  for (const row of rows) {
    if (!row.key) continue;
    const kind = kindOf(row.key)?.kind;

    if (kind === "number") {
      if (row.raw.trim() === "" || Number.isNaN(Number(row.raw))) continue;
      search[row.key] = Number(row.raw);
    } else if (kind === "csv") {
      const items = row.raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (items.length) search[row.key] = items;
    } else if (kind === "range") {
      const range: { min_price?: number; max_price?: number } = {};

      if (row.min.trim() !== "") {
        const min = Number(row.min);
        if (!Number.isNaN(min)) range.min_price = min;
      }
      if (row.max.trim() !== "") {
        const max = Number(row.max);
        if (!Number.isNaN(max)) range.max_price = max;
      }

      // Keep whichever bound is valid instead of discarding the whole row.
      if (Object.keys(range).length > 0) {
        search[row.key] = [range];
      }
    } else if (row.raw.trim() !== "") {
      search[row.key] = row.raw.trim();
    }
  }

  const locked = lockedFacets.filter(Boolean);
  if (Object.keys(search).length === 0 && locked.length === 0) return null;
  return { search, lockedFacets: locked };
}

let rowSeq = 0;
const nextId = () => `f${rowSeq++}`;

const CategoryFiltersEditor: React.FC<CategoryFiltersEditorProps> = ({
  value,
  onChange,
}) => {
  const [rows, setRows] = React.useState<Row[]>(() => toRows(value));
  const [locked, setLocked] = React.useState<string[]>(() =>
    Array.isArray(value?.lockedFacets)
      ? value.lockedFacets.filter(
          (facet): facet is string => typeof facet === "string",
        )
      : [],
  );

  const commit = (nextRows: Row[], nextLocked: string[]) => {
    setRows(nextRows);
    setLocked(nextLocked);
    onChange(toFilters(nextRows, nextLocked));
  };

  const updateRow = (id: string, patch: Partial<Row>) =>
    commit(
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      locked,
    );

  const addRow = () =>
    commit(
      [...rows, { id: nextId(), key: "", raw: "", min: "", max: "" }],
      locked,
    );

  const removeRow = (id: string) =>
    commit(
      rows.filter((r) => r.id !== id),
      locked,
    );

  const toggleLocked = (facet: string) => {
    const next = locked.includes(facet)
      ? locked.filter((f) => f !== facet)
      : [...locked, facet];
    commit(rows, next);
  };

  const usedKeys = new Set(rows.map((r) => r.key).filter(Boolean));

  return (
    <div className="border-t pt-6 space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Categoria Virtual</h3>
        <p className="text-sm text-gray-500 mt-1">
          Opcional. Aplica filtros à categoria pai e esconde os filtros
          correspondentes no frontoffice. Deixa vazio para uma categoria normal.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Filtros de pesquisa
          </label>
          <button
            type="button"
            onClick={addRow}
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            + Adicionar filtro
          </button>
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-gray-400">Sem filtros aplicados.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => {
              const option = kindOf(row.key);
              return (
                <div key={row.id} className="flex items-start gap-2">
                  <select
                    value={row.key}
                    onChange={(e) =>
                      updateRow(row.id, {
                        key: e.target.value,
                        raw: "",
                        min: "",
                        max: "",
                      })
                    }
                    className="w-48 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecionar...</option>
                    {CATEGORY_FILTER_OPTIONS.map((o) => (
                      <option
                        key={o.key}
                        value={o.key}
                        disabled={o.key !== row.key && usedKeys.has(o.key)}
                      >
                        {o.label}
                      </option>
                    ))}
                  </select>

                  {option?.kind === "range" ? (
                    <div className="flex flex-1 items-center gap-2">
                      <input
                        type="number"
                        value={row.min}
                        onChange={(e) =>
                          updateRow(row.id, { min: e.target.value })
                        }
                        placeholder="mín"
                        className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-400">–</span>
                      <input
                        type="number"
                        value={row.max}
                        onChange={(e) =>
                          updateRow(row.id, { max: e.target.value })
                        }
                        placeholder="máx"
                        className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ) : option?.kind === "select" ? (
                    <select
                      value={row.raw}
                      onChange={(e) =>
                        updateRow(row.id, { raw: e.target.value })
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecionar...</option>
                      {option.options?.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={option?.kind === "number" ? "number" : "text"}
                      value={row.raw}
                      onChange={(e) =>
                        updateRow(row.id, { raw: e.target.value })
                      }
                      placeholder={
                        option?.kind === "csv" ? "valor1, valor2" : "Valor"
                      }
                      disabled={!row.key}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    aria-label="Remover filtro"
                    className="mt-1 h-6 w-6 shrink-0 text-gray-400 hover:text-red-600 focus:outline-none"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filtros bloqueados no frontoffice
        </label>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {LOCKABLE_FACETS.map((f) => (
            <label
              key={f.value}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={locked.includes(f.value)}
                onChange={() => toggleLocked(f.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{f.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryFiltersEditor;
